import type { SnackBarConfig } from '@shared/components/Snackbar';
import type { User } from '../types/types.auth';
import { Box, TextField } from '@mui/material';
import axios from 'axios';
import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@shared/components/Buttons/Button';
import { LoadingSpinner } from '@shared/components/LoadingSpinner';
import { UserContext } from '@/shared/contexts/UserContext';
import { authService } from '@shared/services/AuthService';
import { clearUTMData, formatUTMDataForUser, getStoredUTMData } from '@shared/utils/utmTracking';

interface VerificationFormProps {
  email: string;
  password: string;
  formData: any;
  handleSuccess?: (user: User) => void;
  setSnackbar: (config: SnackBarConfig) => void;
  setLoading: (loading: boolean) => void;
  signUpFlag?: string;
  isModal?: boolean;
}

function VerificationForm(props: VerificationFormProps) {
  const { formData, setSnackbar, setLoading, handleSuccess } = props;
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLocalLoading] = useState(false);

  const { setUser, refetchUser } = useContext(UserContext);
  const navigate = useNavigate();

  const validate = () => {
    if (!code.trim()) {
      setError('Code is required');
      return false;
    }
    if (code.length !== 6) {
      setError('Code must be 6 digits');
      return false;
    }
    return true;
  };

  const handleVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate())
      return;

    setLoading(true);
    setLocalLoading(true);

    try {
      const verifyUser = await authService.confirmRegistration(
        formData.email,
        code,
        true,
      );

      if (!verifyUser) {
        throw new Error('User verification failed.');
      }

      // Get UTM data for user creation (includes referrerCode if present)
      const storedUTMData = getStoredUTMData();
      const utmPayload = formatUTMDataForUser(storedUTMData);

      await axios.post(`${import.meta.env.VITE_API_BASE_URL}/user`, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        company: formData.company,
        positionTitle: '',
        role: 'contributor',
        referer: utmPayload.referer || formData.referrer || '',
        source: utmPayload.source || '',
        campaign: utmPayload.campaign || '',
        landingPage: utmPayload.landingPage || '',
        ...(formData.inviteCode && { inviteCode: formData.inviteCode }),
      });

      const user = await authService.authenticateUser(
        formData.email,
        formData.password,
      );
      if ('newPasswordRequired' in user && user.newPasswordRequired) {
        setSnackbar({
          type: 'error',
          message: 'New password is required to complete verification.',
          open: true,
        });
      }
      else {
        if (!('newPasswordRequired' in user)) {
          setUser(user);
          try {
            await refetchUser({
              email: user.email,
              idToken: user.idToken || '',
            });
            if (handleSuccess) {
              handleSuccess(user);
              navigate('/onboarding');
              clearUTMData();
              setLoading(false);
              return;
            }
          }
          catch (fetchError: any) {
            setSnackbar({
              type: 'error',
              message: fetchError?.message || 'Failed to load user data. Please try again.',
              open: true,
            });
            setLoading(false);
            return;
          }
        }
      }

      if (!handleSuccess) {
        // New users should always go through onboarding first
        navigate('/onboarding');
      }
      clearUTMData();
    }
    catch (err) {
      setSnackbar({
        type: 'error',
        message: err?.message || 'Verification failed.',
        open: true,
      });
    }
    finally {
      setLoading(false);
      setLocalLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleVerification} className="auth-form">
      {loading && (
        <div className="loader-overlay">
          <LoadingSpinner variant="circular" size="large" message="Verifying your account..." fullScreen />
        </div>
      )}
      <div className="form-header">
        <h2 className="form-title">Enter Verification Code</h2>
        <p className="form-subtitle">
          A verification code has been sent to your email address. Please enter
          the code below to verify your account.
        </p>
      </div>

      <TextField
        className="input verification-code-input"
        name="code"
        type="text"
        placeholder="000000"
        value={code}
        onChange={(e) => {
          const value = e.target.value.replace(/\D/g, '');
          if (value.length <= 6) {
            setCode(value);
            setError('');
          }
        }}
        error={Boolean(error)}
        helperText={error}
      />

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Button
          type="submit"
          disabled={!code.trim() || loading}
          data-testid="verify-account"
          sx={{ minWidth: 200 }}
        >
          Verify Account
        </Button>
      </Box>
    </Box>
  );
}

export default VerificationForm;
